import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Card } from '@/components/shadcn/card';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import type { MabarParticipant } from '@/services/communityService';
import badmintonService, {
  type BadmintonGameListItem,
  type BadmintonRoundDetail,
} from '@/services/badmintonService';
import BadmintonSetScorePanel from './BadmintonSetScorePanel';
import BadmintonRoundLineupEditor from './BadmintonRoundLineupEditor';
import { roundGames } from './badmintonListUtils';
import {
  formatBadmintonGameStatus,
  formatCourtLabel,
  playersFirstNameLabel,
  playersLabel,
} from './badmintonUiUtils';
import { buildSetsFromGame, countSetWins, type SetScoreInput } from './badmintonScoringUtils';

interface BadmintonRoundCardProps {
  round: BadmintonRoundDetail;
  canEdit: boolean;
  hideLineupEdit?: boolean;
  participants: MabarParticipant[];
  onChanged: () => void;
  onNotify: (message: string, type: 'success' | 'error') => void;
}

const statusBadge: Record<string, string> = {
  completed: 'bg-[#34C759]/15 text-[#34C759]',
  in_progress: 'bg-amber-400/15 text-amber-600',
};

export default function BadmintonRoundCard(props: BadmintonRoundCardProps) {
  const games = roundGames(props.round);
  if (games.length === 0) {
    return (
      <Card className="rounded-xl p-4 text-sm text-gray-500">
        Ronde {props.round.roundNumber} belum memiliki susunan.
      </Card>
    );
  }
  return (
    <>
      {games.map((game) => (
        <BadmintonRoundGameCard key={game.slug} {...props} game={game} />
      ))}
    </>
  );
}

function BadmintonRoundGameCard({
  round,
  game,
  canEdit,
  hideLineupEdit = false,
  participants,
  onChanged,
  onNotify,
}: BadmintonRoundCardProps & { game: BadmintonGameListItem }) {
  const [sets, setSets] = useState<SetScoreInput[]>(
    buildSetsFromGame(game.maxSets, game.setScores ?? [])
  );
  const [saving, setSaving] = useState(false);
  const [lineupOpen, setLineupOpen] = useState(false);

  useEffect(() => {
    setSets(buildSetsFromGame(game.maxSets, game.setScores ?? []));
  }, [game]);

  const labelA = playersLabel(game.sideAPlayers) || 'Sisi A';
  const labelB = playersLabel(game.sideBPlayers) || 'Sisi B';
  const firstNameLabelA = playersFirstNameLabel(game.sideAPlayers) || 'Sisi A';
  const firstNameLabelB = playersFirstNameLabel(game.sideBPlayers) || 'Sisi B';
  const { winsA, winsB } = countSetWins(game.setScores ?? []);
  const courtLabel = formatCourtLabel(game.courtNumber, game.sessionNumCourts);

  const handleSave = async () => {
    setSaving(true);
    try {
      await badmintonService.updateScores(game.slug, sets);
      onNotify('Skor ronde disimpan', 'success');
      onChanged();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      onNotify(msg || 'Gagal menyimpan skor', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="overflow-hidden rounded-xl border-l-4 border-l-[#FF5A1F]">
      <div className="flex items-center justify-between gap-2 px-4 pt-4">
        <span className="text-sm font-bold text-gray-900">
          Ronde {round.roundNumber}
          {courtLabel ? ` · ${courtLabel}` : ''}
        </span>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            statusBadge[game.status] ?? 'bg-gray-100 text-gray-500'
          }`}
        >
          {formatBadmintonGameStatus(game.status)}
        </span>
      </div>

      {!canEdit ? (
        <>
          {/* Mobile Section */}
          <div className="block lg:hidden">
            <div className="flex flex-col gap-2 px-4 py-4">
              <p className="truncate text-left text-sm font-semibold text-gray-800">
                {firstNameLabelA}
              </p>
              <span className="self-center rounded-md bg-gray-700 px-3 py-1.5 text-sm font-bold text-white">
                {winsA} : {winsB}
              </span>
              <p className="truncate text-right text-sm font-semibold text-gray-800">
                {firstNameLabelB}
              </p>
            </div>
          </div>
          {/* Desktop Section */}
          <div className="hidden lg:block">
            <div className="flex items-center justify-between gap-3 px-4 py-4">
              <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{labelA}</p>
              <span className="flex-shrink-0 rounded-md bg-gray-700 px-3 py-1.5 text-sm font-bold text-white">
                {winsA} : {winsB}
              </span>
              <p className="min-w-0 flex-1 truncate text-right text-sm font-semibold text-gray-800">
                {labelB}
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="px-4 py-4">
          <BadmintonSetScorePanel
            game={game}
            sets={sets}
            canEdit={canEdit}
            onSetsChange={setSets}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            <ShadcnButton
              size="sm"
              className="gap-1.5 bg-[#FF5A1F] hover:bg-[#e54e16]"
              onClick={handleSave}
              disabled={saving}
            >
              <Icon icon="mdi:content-save" className="h-4 w-4" />
              {saving ? 'Menyimpan...' : 'Simpan Skor'}
            </ShadcnButton>
            {!hideLineupEdit && (
              <ShadcnButton
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setLineupOpen(true)}
              >
                <Icon icon="mdi:account-switch" className="h-4 w-4" />
                Edit Susunan
              </ShadcnButton>
            )}
          </div>
        </div>
      )}

      {lineupOpen && (
        <BadmintonRoundLineupEditor
          isOpen={lineupOpen}
          onClose={() => setLineupOpen(false)}
          game={game}
          participants={participants}
          onSuccess={() => {
            setLineupOpen(false);
            onChanged();
          }}
          onError={(m) => onNotify(m, 'error')}
        />
      )}
    </Card>
  );
}
